import { ProductDuration, Products } from "./support"

export interface PromoDiscount {
  percent_off?: number
  amount_off?: number
  currency?: string
  duration?: string
  duration_in_months?: number
  name?: string
}

export interface PromoValidation {
  valid?: boolean
  error?: string
  promotion_code_id?: string
  code?: string
  discount?: PromoDiscount
}

export function promoIsApplied(
  validation: PromoValidation | null,
): validation is PromoValidation & { valid: true; discount: PromoDiscount } {
  return Boolean(validation?.valid && validation.discount)
}

export function applyPromoToAmount(
  unitAmount: number,
  priceCurrency: string,
  validation: PromoValidation | null,
): number {
  if (!promoIsApplied(validation)) return unitAmount

  const { discount } = validation

  if (discount.percent_off) {
    return Math.max(0, Math.round(unitAmount * (1 - discount.percent_off / 100)))
  }

  if (discount.amount_off) {
    if (discount.currency && discount.currency.toLowerCase() !== priceCurrency.toLowerCase()) {
      return unitAmount
    }

    return Math.max(0, unitAmount - discount.amount_off)
  }

  return unitAmount
}

export function formatAmountFromCents(unitAmount: number): string {
  return (unitAmount / 100).toFixed(2)
}

export function monthlyEquivalentAmount(unitAmount: number, subscription: ProductDuration): number {
  return subscription === "annually" ? unitAmount / 12 : unitAmount
}

export function annualSavingsAmounts(
  products: Products,
  currencyAcronym: string,
  validation: PromoValidation | null,
): { original: number; discounted: number } {
  const monthlyPrice = products.monthly.prices[currencyAcronym]
  const annualPrice = products.annually.prices[currencyAcronym]

  return {
    original: monthlyPrice.unit_amount * 12 - annualPrice.unit_amount,
    discounted:
      applyPromoToAmount(monthlyPrice.unit_amount, monthlyPrice.currency, validation) * 12 -
      applyPromoToAmount(annualPrice.unit_amount, annualPrice.currency, validation),
  }
}
